import React from 'react'

import {
  Box,
  Button,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material'
import { useFormContext } from '../../context/form'
import { Product } from '../../types/product'
import { formatMonetaryValue, getLoanToValue } from '../../utils/number'

type Props = {
  product: Product
}

const ProductItemInfo = ({ product }: Props) => {
  const { form } = useFormContext()
  const theme = useTheme()

  const [value, setValue] = React.useState(0)

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }

  return (
    <>
      <Tabs
        value={value}
        onChange={handleChange}
        variant="fullWidth"
        TabIndicatorProps={{
          style: {
            backgroundColor: 'transparent',
          },
        }}
        centered
      >
        <Tab
          id={`full-width-tab-0`}
          aria-controls="full-width-tabpanel-0"
          label={
            <Typography
              variant="body1"
              component="span"
              fontWeight={700}
              textTransform="capitalize"
              sx={{
                fontSize: {
                  md: '14px !important',
                  xs: '12px !important',
                },
                mb: '0 !important',
              }}
            >
              Repayments
            </Typography>
          }
          disableRipple
        />
        <Tab
          id={`full-width-tab-1`}
          aria-controls="full-width-tabpanel-1"
          label={
            <Typography
              variant="body1"
              component="span"
              fontWeight={700}
              textTransform="capitalize"
              sx={{
                fontSize: {
                  md: '14px !important',
                  xs: '12px !important',
                },
                mb: '0 !important',
              }}
            >
              Fees & Charges
            </Typography>
          }
          disableRipple
        />
        <Tab
          id={`full-width-tab-2`}
          aria-controls="full-width-tabpanel-2"
          label={
            <Typography
              variant="body1"
              component="span"
              fontWeight={700}
              textTransform="capitalize"
              sx={{
                fontSize: {
                  md: '14px !important',
                  xs: '12px !important',
                },
                mb: '0 !important',
              }}
            >
              Summary
            </Typography>
          }
          disableRipple
        />
      </Tabs>
      <Box
        role="tabpanel"
        flex={1}
        hidden={value !== 0}
        id={`full-width-tabpanel-0`}
        aria-labelledby={`full-width-tab-0`}
        padding="20px"
        sx={{ backgroundColor: 'white' }}
      >
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography
              variant="body1"
              component="span"
              fontSize={{
                md: '14px !important',
                xs: '12px !important',
              }}
            >
              Initial Monthly Payment
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="body1"
              component="span"
              fontWeight={700}
              textAlign="right"
              fontSize={{
                md: '14px !important',
                xs: '12px !important',
              }}
            >
              £{formatMonetaryValue(product.InitialMonthlyPayment)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="body1"
              component="span"
              fontSize={{
                md: '14px !important',
                xs: '12px !important',
              }}
            >
              Ongoing Monthly Payment
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="body1"
              component="span"
              fontWeight={700}
              textAlign="right"
              fontSize={{
                md: '14px !important',
                xs: '12px !important',
              }}
            >
              £{formatMonetaryValue(product.MonthlyPaymentAfterInitialPeriod)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="body1"
              component="span"
              fontSize={{
                md: '14px !important',
                xs: '12px !important',
              }}
            >
              True Cost Over Initial Period
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="body1"
              component="span"
              fontWeight={700}
              textAlign="right"
              fontSize={{
                md: '14px !important',
                xs: '12px !important',
              }}
            >
              £{formatMonetaryValue(product.TrueCostOverInitialPeriod)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="body1"
              component="span"
              fontSize={{
                md: '14px !important',
                xs: '12px !important',
              }}
            >
              Total Amount Repayable
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="body1"
              component="span"
              fontWeight={700}
              textAlign="right"
              fontSize={{
                md: '14px !important',
                xs: '12px !important',
              }}
            >
              £{formatMonetaryValue(product.TrueCostFullTerm)}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Box
        role="tabpanel"
        flex={1}
        hidden={value !== 1}
        id={`full-width-tabpanel-1`}
        aria-labelledby={`full-width-tab-1`}
        padding="20px"
        sx={{ backgroundColor: 'white' }}
      >
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography
              variant="body1"
              component="span"
              fontSize={{
                md: '14px !important',
                xs: '12px !important',
              }}
            >
              Booking Fee
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="body1"
              component="span"
              fontWeight={700}
              textAlign="right"
              fontSize={{
                md: '14px !important',
                xs: '12px !important',
              }}
            >
              £{formatMonetaryValue(product.BookingFee)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="body1"
              component="span"
              fontSize={{
                md: '14px !important',
                xs: '12px !important',
              }}
            >
              Arrangement Fee
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="body1"
              component="span"
              fontWeight={700}
              textAlign="right"
              fontSize={{
                md: '14px !important',
                xs: '12px !important',
              }}
            >
              £{formatMonetaryValue(product.ArrangementFee)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="body1"
              component="span"
              fontSize={{
                md: '14px !important',
                xs: '12px !important',
              }}
            >
              Other Fees
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="body1"
              component="span"
              fontWeight={700}
              textAlign="right"
              fontSize={{
                md: '14px !important',
                xs: '12px !important',
              }}
            >
              £{formatMonetaryValue(product.CHAPSFee + product.DisbursementFee)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="body1"
              component="span"
              fontSize={{
                md: '14px !important',
                xs: '12px !important',
              }}
            >
              Total Fees
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="body1"
              component="span"
              fontWeight={700}
              textAlign="right"
              fontSize={{
                md: '14px !important',
                xs: '12px !important',
              }}
            >
              £
              {formatMonetaryValue(
                product.BookingFee +
                  product.ArrangementFee +
                  product.CHAPSFee +
                  product.DisbursementFee
              )}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="body1"
              component="span"
              fontSize={{
                md: '14px !important',
                xs: '12px !important',
              }}
            >
              Early Repayment Charge
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="body1"
              component="span"
              fontWeight={700}
              textAlign="right"
              fontSize={{
                md: '14px !important',
                xs: '12px !important',
              }}
            >
              {product?.EarlyRepaymentChargeAppliesUntil ? 'Yes' : 'No'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="body1"
              component="span"
              fontSize={{
                md: '14px !important',
                xs: '12px !important',
              }}
            >
              Valuation Fee
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="body1"
              component="span"
              fontWeight={700}
              textAlign="right"
              fontSize={{
                md: '14px !important',
                xs: '12px !important',
              }}
            >
              {product.ValuationFee > 0 ? 'Yes' : 'No'}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Box
        role="tabpanel"
        flex={1}
        hidden={value !== 2}
        id={`full-width-tabpanel-2`}
        aria-labelledby={`full-width-tab-2`}
        padding="20px"
        sx={{ backgroundColor: 'white' }}
      >
        <Stack
          sx={{
            height: 'auto !important',
          }}
          spacing={2}
        >
          <Typography
            variant="body1"
            component="span"
            fontSize={{
              md: '14px !important',
              xs: '12px !important',
            }}
          >
            For the first {product.InitialRatePeriodMonths} (the initial period)
            months of this mortgage, the rate is{' '}
            <strong>{`${Number(product.InitialPayRate).toFixed(2)}`}%</strong>{' '}
            which means your monthly payment will be{' '}
            <strong>
              £{formatMonetaryValue(product.InitialMonthlyPayment)}
            </strong>
            .
          </Typography>
          <Typography
            variant="body1"
            component="span"
            fontSize={{
              md: '14px !important',
              xs: '12px !important',
            }}
          >
            After {product.InitialRatePeriodMonths} months, the rate will move
            onto the Lender's Standard Variable Rate. This is currently{' '}
            <strong>{product.StandardVariableRate}%</strong> (meaning a monthly
            payment of{' '}
            <strong>
              £{formatMonetaryValue(product.MonthlyPaymentAfterInitialPeriod)}
            </strong>
            ).
          </Typography>
          <Typography
            variant="body1"
            component="span"
            fontSize={{
              md: '14px !important',
              xs: '12px !important',
            }}
          >
            The maximum Loan to Value (LTV) is {product.MaxLTVAvailable}% for
            this mortgage.{' '}
            <strong>
              Your LTV is{' '}
              {getLoanToValue(
                form.getValues().propertyValue! || 0,
                form.getValues().loanRequired! || 0
              )}
              %
            </strong>{' '}
            based on the figures you have entered on the left.
          </Typography>
          <Typography
            variant="body1"
            component="span"
            fontSize={{
              md: '14px !important',
              xs: '12px !important',
            }}
          >
            The true cost of this mortgage over the initial period is{' '}
            <strong>
              £{formatMonetaryValue(product.TrueCostOverInitialPeriod)}
            </strong>
            , this includes all fees and payments. This is a good way to compare
            mortgages of the same initial period.
          </Typography>
        </Stack>
      </Box>
      <Box
        marginTop="10px"
        marginLeft="auto"
        marginRight="auto"
        width="fit-content"
      >
        <Button
          href={
            //@ts-ignore
            theme?.custom?.appConfig?.enquireButtonLink ??
            //@ts-ignore
            theme?.custom?.links?.enquireButtonLink
          }
          target="_self"
          variant="contained"
          color="secondary"
          sx={{ marginTop: '10px', margin: 'auto', borderRadius: '20px' }}
        >
          <Typography
            variant="body1"
            component="span"
            fontWeight={700}
            color="white"
            textTransform="capitalize"
            sx={{
              fontSize: {
                md: '14px !important',
                xs: '12px !important',
              },
              mb: '0 !important',
            }}
          >
            Enquire about this mortgage
          </Typography>
        </Button>
      </Box>
    </>
  )
}

export default ProductItemInfo
